<template>
  <transition name="panel">
    <div v-if="isOpen" class="panel-container" :class="computedClassContainer">
      <Overlay v-if="hasOverlay" class="panel-overlay" @click="close" />
      <div
        class="panel-content"
        :style="{
          'background-color': hasContentBackground
            ? 'transparent'
            : contentColor || NebraskaColors.backgroundSecondary
        }"
        :class="computedClass"
        @scroll="handleScroll"
      >
        <slot />
      </div>
    </div>
  </transition>
</template>

<script lang="ts">
import { Vue, Prop, Emit, Component, Watch } from 'vue-property-decorator';
import { Overlay } from '@warrenbrasil/nebraska-web';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import {
  toggleBodyScroll,
  addBodyScroll
} from '@/modules/common/helpers/browser';

type Position = 'top' | 'right' | 'bottom' | 'left';
type Container = 'screen-size' | '';
type ContentSize = 'md' | '';

interface ScrollTarget {
  target: {
    scrollTop: number;
    clientHeight: number;
    scrollHeight: number;
  };
}

@Component({
  components: {
    Overlay
  }
})
export default class SideSheet extends Vue {
  @Prop({ type: Boolean, default: true })
  public hasOverlay!: boolean;

  @Prop({ type: Boolean, default: false })
  public isOpen!: boolean;

  @Prop({ type: String, default: 'top' })
  public position!: Position;

  @Prop({ type: String, default: '' })
  public containerClass!: Container;

  @Prop({ type: String, default: '' })
  public contentSize!: ContentSize;

  @Prop({ type: String, default: '' })
  public contentColor!: string;

  @Prop({ type: Boolean, default: false })
  public hasContentBackground?: boolean;

  private NebraskaColors = NebraskaColors;

  @Watch('isOpen')
  private toggleScroll() {
    toggleBodyScroll();
  }

  @Emit()
  private close() {
    return false;
  }

  private handleScroll({ target }: ScrollTarget) {
    const { scrollTop, clientHeight, scrollHeight } = target;
    this.$emit('scroll', target.scrollTop);

    if (scrollTop + clientHeight >= scrollHeight) {
      this.$emit('bottom');
    }
  }

  private get computedClass(): string[] {
    const classes = [];

    classes.push(this.position);
    classes.push(this.contentSize);

    return classes;
  }

  private get computedClassContainer(): string[] {
    const classes = [];

    classes.push(this.containerClass);

    return classes;
  }

  destroyed() {
    addBodyScroll();
  }
}
</script>

<style lang="less" scoped>
@time: 400ms;
@ease: ease-in-out;

.panel-container {
  overflow: hidden;
  position: fixed;
  top: 0;
  left: 0;
  transition: all @time;
  width: 100%;
  height: 100%;
  z-index: 3000;

  &.screen-size {
    width: 100vw;
    height: 100vh;
  }
}

.panel-overlay {
  transition: opacity @time @ease;
}

.panel-content {
  background: @background-secondary;
  overflow: hidden;
  position: absolute;
  transition: transform @time @ease;
  width: 100%;
  height: 100%;
  z-index: 1;

  &.top,
  &.bottom {
    height: auto;
    min-height: 60px;
  }

  &.left,
  &.right {
    overflow-y: auto;

    @media @tabletUp {
      width: 360px;
    }

    &.md {
      @media @tabletUp {
        width: 680px;
      }
    }
  }

  &.top,
  &.left {
    top: 0;
    left: 0;
  }

  &.right {
    top: 0;
    right: 0;
  }

  &.bottom {
    bottom: 0;
    left: 0;
  }
}

.panel-enter,
.panel-leave-to {
  .panel-overlay {
    opacity: 0;
  }
  .left {
    transform: translate(-100%, 0);
  }
  .right {
    transform: translate(100%, 0);
  }
  .top {
    transform: translate(0, -100%);
  }
  .bottom {
    transform: translate(0, 100%);
  }
}
</style>
